import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Filter, RotateCcw } from 'lucide-react';
import { ProfessionalFilters } from '@/types/professional';

interface ProfessionalFilterSidebarProps {
  filters: ProfessionalFilters;
  onFiltersChange: (filters: ProfessionalFilters) => void;
  onFiltersReset: () => void;
}

const specialtiesOptions = [
  'Fall Prevention',
  'Home Safety Assessment',
  'Mobility Training',
  'Cognitive Assessment',
  'Bathroom Modifications',
  'Kitchen Adaptations',
  'Stair Safety',
  'Lighting Improvements'
];


export function ProfessionalFilterSidebar({ filters, onFiltersChange, onFiltersReset }: ProfessionalFilterSidebarProps) {
  const updateFilter = (key: keyof ProfessionalFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const updateArrayFilter = (key: keyof ProfessionalFilters, value: string, checked: boolean) => {
    const currentArray = (filters[key] as string[]) || [];
    if (checked) {
      updateFilter(key, [...currentArray, value]);
    } else {
      updateFilter(key, currentArray.filter(item => item !== value));
    }
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.licenseVerificationStatus && filters.licenseVerificationStatus !== 'all') count++;
    if (filters.professionalRole && filters.professionalRole !== 'all') count++;
    if (filters.specialties?.length) count++;
    if (filters.serviceRadius !== undefined) count++;
    return count;
  };

  return (
    <Card className="rounded-2xl border bg-white shadow-sm">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Filter className="w-5 h-5 text-muted-foreground" />
            Filters
            {getActiveFilterCount() > 0 && (
              <Badge variant="secondary" className="ml-2">
                {getActiveFilterCount()}
              </Badge>
            )}
          </CardTitle>
          {getActiveFilterCount() > 0 && (
            <Button variant="ghost" size="sm" onClick={onFiltersReset} className="gap-1">
              <RotateCcw className="w-3 h-3" />
              Reset all
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* License Verification Status */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-st_taupe">License Status</legend>
          <Select
            value={filters.licenseVerificationStatus || 'all'}
            onValueChange={(value) => updateFilter('licenseVerificationStatus', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="not_verified">Not Verified</SelectItem>
            </SelectContent>
          </Select>
        </fieldset>

        {/* Professional Role */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-st_taupe">Professional Type</legend>
          <Select
            value={filters.professionalRole || 'all'}
            onValueChange={(value) => updateFilter('professionalRole', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="caregiver_agency">Caregiver / Home Care Agency</SelectItem>
              <SelectItem value="care_manager">Care Manager</SelectItem>
              <SelectItem value="lcsw">Licensed Clinical Social Worker</SelectItem>
              <SelectItem value="ot">Occupational Therapist</SelectItem>
              <SelectItem value="pt">Physical Therapist</SelectItem>
              <SelectItem value="aging_consultant">Aging Consultant / Geriatric Specialist</SelectItem>
              <SelectItem value="financial_planner">Financial Planner</SelectItem>
              <SelectItem value="home_health">Home Health / Senior Services</SelectItem>
              <SelectItem value="patient_advocate">Patient Advocate</SelectItem>
              <SelectItem value="elder_law">Elder Law Attorney</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </fieldset>

        {/* Specialties */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-st_taupe">Specialties</legend>
          <div className="grid grid-cols-1 gap-2">
            {specialtiesOptions.map((specialty) => (
              <div key={specialty} className="flex items-center space-x-2">
                <Checkbox
                  id={`specialty-${specialty}`}
                  checked={(filters.specialties || []).includes(specialty)}
                  onCheckedChange={(checked) => 
                    updateArrayFilter('specialties', specialty, checked as boolean)
                  }
                />
                <Label 
                  htmlFor={`specialty-${specialty}`}
                  className="text-sm cursor-pointer min-h-[44px] flex items-center"
                >
                  {specialty}
                </Label>
              </div>
            ))}
          </div>
        </fieldset>

        {/* Service Radius */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-st_taupe">Service Radius (miles)</legend>
          <Input
            type="number"
            placeholder="25"
            value={filters.serviceRadius || ''}
            onChange={(e) => updateFilter('serviceRadius', e.target.value ? Number(e.target.value) : undefined)}
          />
        </fieldset>

        {/* Actions */}
        <div className="pt-4 border-t">
          <Button 
            className="w-full bg-st_light_blue hover:bg-st_light_blue/90 text-white" 
            size="lg"
          >
            Apply filters
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}